import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Mirrors the backend's format_event_date so the preview matches what is sent.
 *
 * Renders in the offset the event was saved with (the venue's local time), NOT
 * the viewer's timezone — `new Date()` would shift an IST event for a UTC
 * browser and the preview would disagree with the delivered message.
 */
function formatEventDate(raw) {
  if (!raw) return "";
  const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!m) return String(raw);
  const [, y, mon, day, hh, mm] = m;
  const monthIdx = Number(mon) - 1;
  if (monthIdx < 0 || monthIdx > 11) return String(raw);
  const h24 = Number(hh);
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${Number(day)} ${MONTHS[monthIdx]} ${y}, ${h12}:${mm} ${period}`;
}

/**
 * Send an approved WhatsApp template to a single lead. Templates are the only
 * way to reach a contact once the 24-hour session window has closed.
 *
 * `lead` needs at least {id, name}; `event` (optional) fills the programme
 * variables in the preview.
 */
export function SendTemplateDialog({ lead, event, trigger, onSent }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/templates")
      .then(({ data }) => setTemplates(data.filter((t) => t.approval_status === "approved")))
      .catch((err) => toast.error(formatApiError(err)));
  }, [open]);

  const selected = templates.find((t) => t.id === tplId);

  // Mirrors the server's substitution so the preview matches what is sent.
  function preview(tpl) {
    if (!tpl || !lead) return "";
    const named = {
      name: (lead.name || "").split(" ")[0],
      full_name: lead.name || "",
      event: event?.name || "",
      date: formatEventDate(event?.date),
      venue: event?.venue || "",
    };
    let body = tpl.body || "";
    for (const [k, v] of Object.entries(named)) {
      body = body.split(`{{${k}}}`).join(v);
    }
    [named.name, named.event, named.date, named.venue].forEach((v, i) => {
      body = body.replace(new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, "g"), v);
    });
    return body;
  }

  async function submit() {
    if (!tplId) return;
    setSending(true);
    try {
      await api.post("/inbox/send-template", { lead_id: lead.id, template_id: tplId });
      toast.success("Template sent");
      setOpen(false);
      setTplId("");
      onSent?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send a template</DialogTitle>
          <DialogDescription>
            Templates reach the lead even outside the 24-hour window — use one to re-engage a
            contact who has gone quiet.
          </DialogDescription>
        </DialogHeader>

        {templates.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            No approved templates yet. Create one on the Templates page, or sync your approved
            templates from Jalpi.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Template</Label>
              <Select value={tplId} onValueChange={setTplId}>
                <SelectTrigger data-testid="template-picker">
                  <SelectValue placeholder="Choose an approved template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} · {t.kind}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selected && (
              <div className="space-y-2">
                <Label>Preview</Label>
                <div className="rounded-lg border border-border bg-accent/40 p-3 text-sm leading-relaxed">
                  {selected.header_type === "text" && selected.header_text && (
                    <div className="mb-1.5 font-semibold" data-testid="template-preview-header">
                      {selected.header_text}
                    </div>
                  )}
                  {selected.header_type && selected.header_type !== "text" && (
                    <div className="mb-1.5 text-xs uppercase tracking-widest text-muted-foreground">
                      [{selected.header_type} header]
                    </div>
                  )}
                  <div className="whitespace-pre-wrap" data-testid="template-preview">
                    {preview(selected)}
                  </div>
                  {selected.footer && (
                    <div
                      className="mt-2 text-xs text-muted-foreground"
                      data-testid="template-preview-footer"
                    >
                      {selected.footer}
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Variables are filled from this lead and their most recent programme.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button onClick={submit} disabled={!tplId || sending} data-testid="template-send-submit">
            {sending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
